import { apiDelete, apiFetch, apiPost } from "./api";

const BASE = "/admin/imports";

export interface ImportsOverview {
  imports_dir: string;
  free_bytes: number;
  max_chunk_bytes: number;
  worker_alive: boolean;
  worker_seen_at: string | null;
}

export type Compression = "none" | "gzip";

export interface UploadInfo {
  id: string;
  file_name: string;
  original_name: string;
  source_size: number;
  chunk_size: number;
  compression: Compression;
  next_chunk: number;
  received_bytes: number;
  stored_bytes: number;
  status: "uploading" | "complete" | "cancelled";
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface DumpFile {
  name: string;
  size: number;
  modified_at: string;
  in_use: boolean;
}

export type RawEmail = "repair" | "all" | "none";

export interface ImportParams {
  file: string;
  dry_run: boolean;
  attachments: boolean;
  inline_images: boolean;
  raw_email: RawEmail;
}

export type JobStatus = "queued" | "running" | "succeeded" | "failed" | "interrupted" | "cancelled";

export interface Job {
  id: number;
  kind: "import" | "rollback";
  status: JobStatus;
  params: Partial<ImportParams>;
  progress: { stage?: string; done?: number; total?: number };
  result: {
    status?: string;
    counts?: Record<string, number>;
    warnings?: string[];
    seconds?: number;
    tickets_removed?: number;
    tickets_restored?: number;
  } | null;
  error: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  heartbeat_at: string | null;
  log: string | null;
}

export interface ImportBatch {
  id: number;
  status: "running" | "succeeded" | "failed" | "rolled_back";
  dry_run: boolean;
  source_file: string;
  source_size: number;
  source_variant: string | null;
  started_at: string;
  finished_at: string | null;
  counts: Record<string, number>;
  error: string | null;
  can_rollback: boolean;
}

export const importsApi = {
  overview: () => apiFetch<ImportsOverview>(`${BASE}/overview`),
  uploads: () => apiFetch<UploadInfo[]>(`${BASE}/uploads`),
  startUpload: (body: {
    original_name: string;
    size: number;
    chunk_size: number;
    compression: Compression;
  }) => apiPost<UploadInfo>(`${BASE}/uploads`, body),
  putChunk: (id: string, index: number, body: Blob, sha256: string | null, signal?: AbortSignal) =>
    apiFetch<UploadInfo>(`${BASE}/uploads/${id}/chunks/${index}`, {
      method: "PUT",
      body,
      signal,
      headers: {
        "Content-Type": "application/octet-stream",
        ...(sha256 ? { "X-Chunk-Sha256": sha256 } : {}),
      },
    }),
  completeUpload: (id: string) => apiPost<UploadInfo>(`${BASE}/uploads/${id}/complete`),
  cancelUpload: (id: string) => apiDelete(`${BASE}/uploads/${id}`),
  files: () => apiFetch<DumpFile[]>(`${BASE}/files`),
  deleteFile: (name: string) => apiDelete(`${BASE}/files/${encodeURIComponent(name)}`),
  jobs: () => apiFetch<Job[]>(`${BASE}/jobs`),
  job: (id: number) => apiFetch<Job>(`${BASE}/jobs/${id}`),
  startImport: (params: ImportParams) => apiPost<Job>(`${BASE}/jobs`, { kind: "import", ...params }),
  startRollback: () => apiPost<Job>(`${BASE}/jobs`, { kind: "rollback" }),
  cancelJob: (id: number) => apiPost<Job>(`${BASE}/jobs/${id}/cancel`),
  batches: () => apiFetch<ImportBatch[]>(`${BASE}/batches`),
};
